/**
 * Answer normalisation — a verbatim port of the prototype's `norm()`.
 *
 * This function is the contract between the client (practice mode) and the
 * Durable Object (duel decks). If the two sides ever disagree on what counts
 * as a match, a duel scores differently for each player. Change it in one
 * place, and only with the table test in test/norm.test.ts updated to match.
 *
 * Lowercase, trim, strip accents, turn hyphens into spaces, drop anything
 * else outside [a-z0-9 ] or Hangul, collapse whitespace, then remove a leading
 * article.
 *
 * Hyphens become spaces rather than vanishing: the answer lists write
 * compounds with a space ("self taught", "all inclusive"), and a learner who
 * spells them the dictionary way, with a hyphen, must not be marked wrong.
 * Apostrophes are simply dropped ("one's" -> "ones"), on both sides alike.
 *
 * Hangul survives: the Korean deck's answers are typed in Hangul. NFD splits a
 * syllable into jamo, which the accent filter leaves alone but the character
 * filter would drop, so the string is recomposed (NFC) before filtering.
 */
export function norm(s: string): string {
  return (s || "")
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .normalize("NFC")
    .replace(/[-\u2010-\u2015]/g, " ")
    .replace(/[^a-z0-9\uac00-\ud7a3 ]/g, "")
    .replace(/\s+/g, " ")
    .replace(/^(to|the|a|an) /, "");
}

/** True when `typed` matches any accepted answer after normalisation. */
export function isCorrect(typed: string, answers: readonly string[]): boolean {
  const t = norm(typed);
  return t.length > 0 && answers.some((a) => norm(a) === t);
}
